import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { Button, Icon, Popover } from 'grab-ui';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import { enableDeliveryGuaranteeSelector } from 'selectors/app-selector';
import NewBookingDuck from 'ducks/new-booking-duck';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _some from 'lodash/some';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import styles from './bulk-actions.less';

const BulkActions = ({ dispatch, deliveryIDs, deliveries }) => {
  const onAction = useCallback(
    overlayType => () => {
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(deliveryIDs, overlayType),
      );
      switch (overlayType) {
        case newBookingOverlayType.serviceConfiguration: {
          dispatch(
            scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliveryEditServiceType(),
          );
          break;
        }
        case newBookingOverlayType.vehicleType: {
          dispatch(
            scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliveryEditVehicleType(),
          );
          break;
        }
        case newBookingOverlayType.addOn: {
          dispatch(
            scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliveryEditAddOn(),
          );
          break;
        }
      }
    },
    [dispatch, deliveryIDs],
  );

  // temporary flag
  // const enableInsuranceFee = true;
  const enableInsuranceFee = useSelector(enableDeliveryGuaranteeSelector);

  const isSelectedServiceType = useMemo(
    () => {
      if (!deliveries || deliveries.length <= 0) {
        return false;
      }
      return _every(
        deliveries,
        delivery =>
          _get(delivery, 'detail.configuration.serviceType') &&
          _get(delivery, 'detail.configuration.specialServiceType'),
      );
    },
    [deliveries],
  );

  const editAddOnsButton = useMemo(
    () => {
      if (!deliveries || deliveries.length <= 0) {
        return undefined;
      }
      const addOnsList = deliveries.map(delivery =>
        _get(delivery, 'config.additionalServices', []).filter(
          addOn => addOn.id !== codAdditionalServiceID,
        ),
      );

      if (_every(addOnsList, addOns => addOns.length === 0)) {
        return undefined;
      }
      const haveSomeAvailableAddOns = _every(addOnsList, addOns =>
        _some(addOns, addOn => !_get(addOn, 'errorMessage')),
      );

      const editButton = (
        <Button
          className={styles.button}
          onClick={onAction(newBookingOverlayType.addOn)}
          disabled={!isSelectedServiceType || !haveSomeAvailableAddOns}
        >
          {I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_button_edit_addons_happypath',
          )}
          <Icon type="right" />
        </Button>
      );

      if (!isSelectedServiceType) {
        return (
          <Popover
            content={I18n.t('bulkUploadRevamp.step3.tooltip.notSelectService')}
            placement={'bottomLeft'}
            overlayClassName={styles.popover}
          >
            {editButton}
          </Popover>
        );
      }
      return haveSomeAvailableAddOns ? (
        editButton
      ) : (
        <Popover
          content={I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_no_available_addons_addons',
          )}
          placement={'bottomLeft'}
          overlayClassName={styles.popover}
        >
          {editButton}
        </Popover>
      );
    },
    [deliveries, isSelectedServiceType, onAction],
  );

  const editVehicleTypeButton = useMemo(
    () => {
      const vehicleButton = (
        <Button
          className={styles.button}
          onClick={onAction(newBookingOverlayType.vehicleType)}
          disabled={!isSelectedServiceType}
        >
          {I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_button_edit_vehicle_type_happypath',
          )}
          <Icon type="right" />
        </Button>
      );
      if (isSelectedServiceType) {
        return vehicleButton;
      }
      return (
        <Popover
          content={I18n.t('bulkUploadRevamp.step3.tooltip.notSelectService')}
          placement={'bottomLeft'}
          overlayClassName={styles.popover}
        >
          {vehicleButton}
        </Popover>
      );
    },
    [isSelectedServiceType, onAction],
  );

  const editGuaranteeFeeButton = useMemo(
    () => {
      const guaranteeFeeButton = (
        <Button
          className={styles.button}
          onClick={onAction(newBookingOverlayType.deliveryGuarantee)}
          disabled={!isSelectedServiceType}
        >
          {I18n.t('v2.anytimeBooking.insurance.editDeliveryGuarantee')}
          <Icon type="right" />
        </Button>
      );
      if (isSelectedServiceType) {
        return guaranteeFeeButton;
      }
      return (
        <Popover
          content={I18n.t('bulkUploadRevamp.step3.tooltip.notSelectService')}
          placement={'bottomLeft'}
          overlayClassName={styles.popover}
        >
          {guaranteeFeeButton}
        </Popover>
      );
    },
    [isSelectedServiceType, onAction],
  );

  if (!deliveryIDs || deliveryIDs.length <= 0) {
    return null;
  }

  return (
    <div className={styles.bulkActions}>
      <span className={styles.selectedCount}>
        {I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_body_amt_of_deliveries_selected_happypath',
          {
            amountOfDeliveries: deliveryIDs.length,
          },
        )}
      </span>
      <Button
        className={styles.button}
        onClick={onAction(newBookingOverlayType.serviceConfiguration)}
      >
        {I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_button_edit_del_options_happypath',
        )}
        <Icon type="right" />
      </Button>
      {editVehicleTypeButton}
      {editAddOnsButton}
      {enableInsuranceFee && editGuaranteeFeeButton}
    </div>
  );
};

BulkActions.propTypes = {
  dispatch: PropTypes.func.isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
}))(BulkActions);
